'use client'

import Image from 'next/image'
import { useState } from 'react'

import { services } from '@/data/services'
import { SectionHead } from '@/ui/components/marks'

/**
 * Le registre des corps d'état.
 *
 * Les six métiers sont présentés comme les lignes d'un relevé plutôt
 * qu'en grille de cartes : la liste est la forme naturelle d'un « tous
 * corps d'état ». Sur desktop, la ligne survolée ou focalisée pilote
 * l'image du panneau de droite.
 */
export default function Services() {
  const [active, setActive] = useState(0)

  return (
    <section className="bg-chalk band" id="services">
      <div className="shell">
        <SectionHead cote="6 corps d'état" title="Nos prestations">
          <p>
            Basée à Septeuil, Nico Bat est une entreprise générale du bâtiment
            tous corps d&apos;État. Nous intervenons dans le département des
            Yvelines, pour vos travaux de plomberie, d&apos;électricité, de
            menuiserie, de vitrerie et de serrurerie.
          </p>
        </SectionHead>

        <div className="mt-14 desktop:mt-20 grid desktop:grid-cols-[1fr_26rem] desktop:gap-16 desktop:items-start">
          {/* <ul> et non <dl> : la structure imbriquée (vignette +
              colonne texte) sortait les <dt>/<dd> de leur parent
              direct, ce qui invalide une liste de définitions. */}
          <ul className="border-t border-ink/15">
            {services.map((service, index) => {
              const isActive = active === index

              return (
                // La ligne n'est pas interactive (aucune page métier
                // dédiée) : pas de tabIndex, le panneau image reste un
                // enrichissement décoratif au survol.
                <li
                  key={service.title}
                  className="group border-b border-ink/15 transition-colors duration-300 hover:bg-ink/3"
                  onMouseEnter={() => setActive(index)}
                >
                  <div className="flex gap-5 py-7 tablet:py-8">
                    {/* Vignette : seule image visible sur mobile */}
                    <div className="relative size-20 shrink-0 overflow-hidden rounded-[3px] desktop:hidden">
                      <Image
                        src={service.imageUrl}
                        alt={service.alt}
                        fill
                        quality={75}
                        sizes="80px"
                        className="object-cover"
                        draggable={false}
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-4">
                        <service.icon
                          aria-hidden="true"
                          className={`size-5 shrink-0 translate-y-0.5 transition-colors duration-300 ${
                            isActive ? 'text-signal' : 'text-graphite'
                          }`}
                          strokeWidth={1.5}
                        />
                        <h3 className="font-display text-2xl tablet:text-3xl font-bold tracking-tight text-ink">
                          {service.title}
                        </h3>
                      </div>
                      <p className="mt-3 max-w-[52ch] text-[0.9375rem] leading-relaxed text-anthracite tablet:pl-9">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>

          {/* Panneau image : desktop uniquement, piloté par la ligne active */}
          {/* next/image fill exige un parent positionné en relative :
              le conteneur sticky ne convient pas, d'où l'enveloppe. */}
          <div className="hidden desktop:block sticky top-24" aria-hidden="true">
            <div className="relative aspect-4/5 overflow-hidden rounded-[3px] bg-ink">
              {services.map((service, index) => (
                <Image
                  key={service.imageUrl}
                  src={service.imageUrl}
                  alt=""
                  fill
                  quality={80}
                  sizes="26rem"
                  className={`object-cover transition-opacity duration-500 ${
                    active === index ? 'opacity-100' : 'opacity-0'
                  }`}
                  draggable={false}
                />
              ))}
              <div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-ink to-transparent p-6 pt-16">
                <span className="font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-chalk">
                  {services[active].title}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
